import { t } from '@/i18n';
import type { z } from 'zod';
import {
  FundBusinessForInvestmentPerformanceTitleModifier,
  type FundBusinessRowModel,
  type fundBusinessForInvestmentPerformanceType,
  type fundBusinessForm,
} from '../_model/fund-business';

const initialAmountInfo = { count: null, amount: null };

export const generateYearAmountInfos = () => {
  return Array.from({ length: 5 }, () => {
    return {
      amountInfo: initialAmountInfo,
    };
  });
};

export const generateBusinessSubsidyEntry = () => {
  return Array.from({ length: 5 }).map((_, fundBusinessIndex) => {
    const yearAmountInfos = Array.from({
      length: 5,
    }).map((_, yearAmountInfoIndex) => {
      const isDisabled = yearAmountInfoIndex < fundBusinessIndex;

      return {
        amountInfo: isDisabled ? undefined : initialAmountInfo,
      };
    });

    return {
      id: `fundBusinessSubsidy-${fundBusinessIndex}`,
      title: null,
      decidedAmountInfo: initialAmountInfo,
      estimatedAmountInfo: initialAmountInfo,
      yearAmountInfos,
    };
  });
};

export const initialFundBusinessSubsidyTable = generateBusinessSubsidyEntry();

export const initialFundBusinessInvestmentTable = [
  t('new-investment'),
  `${t('new-investment')}(${t('initial-estimate')})`,
  t('investment-redemption'),
  t('damage-to-investment'),
  t('investment-balance'),
].map((title, index) => ({
  id: `fundBusinessInvestment-${index}`,
  title,
  yearAmountInfos: generateYearAmountInfos(),
}));

export const initialFundBusinessDebtGuaranteeTable = [
  t('new-debt-guarantee'),
  t('initial-estimate'),
  t('end-of-debt-guarantee'),
  t('new-subrogation-payment'),
  t('debt-guarantee-balance'),
].map((title, index) => ({
  id: `fundBusinessDebtGuarantee-${index}`,
  title,
  yearAmountInfos: generateYearAmountInfos(),
}));

export const initialFundBusinessLoanTable = [
  t('new-loan'),
  t('initial-estimate'),
  t('loan-collection'),
  t('new-bad-debts'),
  t('loan-balance'),
].map((title, index) => ({
  id: `fundBusinessLoan-${index}`,
  title,
  yearAmountInfos: generateYearAmountInfos(),
}));

export const initialFundBusinessOthersTable = [
  t('fund-business-others-new-label'),
  t('fund-business-others-new-initial-estimate-label'),
  t('fund-business-others-redemption-label'),
  t('fund-business-others-damage-label'),
  t('fund-business-others-balance-label'),
].map((title, index) => ({
  id: `fundBusinessOthers-${index}`,
  title,
  yearAmountInfos: generateYearAmountInfos(),
}));

export const getInitialPerformancesData = (
  businessForm: z.infer<typeof fundBusinessForm>,
) => {
  switch (businessForm) {
    case 'subsidy':
      return initialFundBusinessSubsidyTable;
    case 'investment':
      return initialFundBusinessInvestmentTable;
    case 'debt-guarantee':
      return initialFundBusinessDebtGuaranteeTable;
    case 'loan':
      return initialFundBusinessLoanTable;
    case 'others':
      return initialFundBusinessOthersTable;
  }
};

export const getInitialPerformancesDataForInvestment = (): z.infer<
  typeof FundBusinessRowModel
>[] => {
  const generatePerformance = (
    type: z.infer<typeof fundBusinessForInvestmentPerformanceType>,
    index: number,
  ) => ({
    id: `fundBusinessInvestment-${index}-${type}`,
    title: FundBusinessForInvestmentPerformanceTitleModifier[type],
    yearAmountInfos: [
      {
        amountInfo: initialAmountInfo,
      },
    ],
  });

  return [...Array(3)].flatMap((_, index) => {
    return [
      generatePerformance('decision', index),
      generatePerformance('execution', index),
    ];
  });
};
